import clsx from 'clsx';

import DrupalImage from '@commons/components/Image/Drupal';
import RichText from '@commons/components/RichText';
import Separator from '@commons/components/Separator';
import Flex from '@commons/containers/Flex';
import { breakpoints } from '@commons/contexts/responsive';
import { Link } from '@commons/i18n/navigation';
import type {
  DrupalImage as DrupalImageType,
  DrupalLink,
  DrupalParagraph,
} from '@commons/types/drupal';
import type { Alignment, Theme } from '@commons/types/styles';
import { API_WEBSITE } from '@commons/utils/constants';

import styles from './index.module.scss';

/**
 * Next.js Image `sizes` property for the Image Tile component.
 *
 * Image tiles are mostly used in a 3-column layout, so we can use the same sizes property.
 *
 * @see https://nextjs.org/docs/app/api-reference/components/image#sizes
 */

const TILE_IMAGE_SIZES = `(max-width: ${breakpoints.lg}px) 100vw, 600px`;

// Type definition for the ImageTileParagraph paragraph properties.

type Props = DrupalParagraph & {
  description: string;
  theme: Theme;
  link?: DrupalLink;
  image: DrupalImageType;
  animated: boolean;
  separator?: boolean;
  alignment?: Alignment;
};

/**
 * ImageTileParagraph paragraph component.
 *
 * Note: This is a copy of TileBlock paragraph.
 */

const ImageTileParagraph = ({ theme, description, link, image, alignment, separator }: Props) => {
  const classes = clsx(
    styles.iconTile,
    styles[`${API_WEBSITE}`],
    link && styles.hasLink,
    'w-100',
    'h-100'
  );

  const renderedCard = (
    <Flex
      theme={theme}
      padding="md"
      justify="space-between"
      direction="column"
      paddingRight={{ xs: 'md', lg: 'lg' }}
      className={classes}
    >
      <Flex direction="column" justify={alignment} className={styles.content}>
        {separator && (
          <Separator dash color="primary" marginBottom="xs" className={styles.separator} />
        )}
        <RichText value={description} />
      </Flex>
      {image && (
        <div className={clsx(styles.backgroundImage, 'h-100', 'w-100')}>
          <DrupalImage {...image} sizes={TILE_IMAGE_SIZES} />
        </div>
      )}
    </Flex>
  );

  if (link) {
    return <Link href={link.url}>{renderedCard}</Link>;
  }

  return renderedCard;
};

export default ImageTileParagraph;
